import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

const C = {
  ink: '#0F172A', slate: '#334155', sub: '#64748B', muted: '#94A3B8',
  line: '#E8EAEE', bg: '#FFFFFF', field: '#F8FAFC', primary: '#4F46E5', danger: '#DC2626',
}

const POINTS = [
  ['📋', 'Run performance reviews', 'PDRs, self-assessment and manager sign-off'],
  ['🧩', 'Assign & track work', 'Jobs, planners and daily check-ins'],
  ['🔒', 'Free to start', 'No credit card required'],
]

export default function Signup() {
  const navigate = useNavigate()
  const { signup, login } = useAuth()
  const [form, setForm] = useState({ fullName: '', email: '', password: '', orgName: '', accessCode: '' })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  function handle(e) { setForm({ ...form, [e.target.name]: e.target.value }) }

  async function handleSignup(e) {
    e.preventDefault()
    setLoading(true); setError('')
    try {
      await signup(form)
      await login(form.email, form.password)
      navigate('/dashboard')
    } catch (err) { setError(err.message); setLoading(false) }
  }

  const label = { display: 'block', fontSize: 13, fontWeight: 600, color: C.slate, marginBottom: 7 }
  const input = { display: 'block', width: '100%', padding: '12px 14px', background: C.field, border: `1px solid ${C.line}`, borderRadius: 10, fontSize: 14, color: C.ink, fontFamily: 'Inter, system-ui', boxSizing: 'border-box', outline: 'none' }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: "'Inter', system-ui, sans-serif", background: C.bg }}>
      {/* Brand panel */}
      <div className="eh-auth-aside" style={{ flex: 1, background: C.ink, flexDirection: 'column', justifyContent: 'space-between', padding: '3rem' }}>
        <div onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 11, cursor: 'pointer' }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: '#fff', fontWeight: 800, fontSize: 13 }}>EH</span>
          </div>
          <span style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>Employee Hive</span>
        </div>

        <div>
          <h1 style={{ fontSize: 30, fontWeight: 800, color: '#fff', lineHeight: 1.25, letterSpacing: '-0.5px', margin: '0 0 14px', maxWidth: 400 }}>
            Set up your organization in under a minute.
          </h1>
          <p style={{ fontSize: 15, color: '#94A3B8', lineHeight: 1.6, margin: '0 0 2.5rem', maxWidth: 380 }}>
            Create your workspace, invite your team, and start running reviews today.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {POINTS.map(([icon, title, desc]) => (
              <div key={title} style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{icon}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#E2E8F0' }}>{title}</div>
                  <div style={{ fontSize: 13, color: '#64748B', marginTop: 1 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ fontSize: 12, color: '#475569' }}>© {new Date().getFullYear()} Employee Hive</div>
      </div>

      {/* Form panel */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          <div style={{ marginBottom: '1.75rem' }}>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: C.ink, margin: '0 0 6px', letterSpacing: '-0.4px' }}>Create your account</h2>
            <p style={{ fontSize: 14, color: C.sub, margin: 0 }}>You'll be set up as the employer for your organization.</p>
          </div>

          <form onSubmit={handleSignup}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: '1.1rem' }}>
              <div>
                <label style={label}>Full name</label>
                <input className="eh-input" name="fullName" value={form.fullName} onChange={handle} placeholder="Mohammed Asif" required style={input} />
              </div>
              <div>
                <label style={label}>Organization</label>
                <input className="eh-input" name="orgName" value={form.orgName} onChange={handle} placeholder="Apex Solutions" required style={input} />
              </div>
            </div>
            <div style={{ marginBottom: '1.1rem' }}>
              <label style={label}>Work email</label>
              <input className="eh-input" name="email" type="email" value={form.email} onChange={handle} placeholder="you@company.com" required style={input} />
            </div>
            <div style={{ marginBottom: '1.1rem' }}>
              <label style={label}>Password</label>
              <input className="eh-input" name="password" type="password" value={form.password} onChange={handle} placeholder="At least 6 characters" required minLength={6} style={input} />
            </div>
            <div style={{ marginBottom: '1.3rem' }}>
              <label style={label}>Sign-up access code</label>
              <input className="eh-input" name="accessCode" value={form.accessCode} onChange={handle} placeholder="Code provided by Employee Hive" required style={input} />
              <p style={{ fontSize: 12, color: C.muted, margin: '6px 0 0' }}>Required to create an organization.</p>
            </div>

            {error && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: C.danger, marginBottom: '1.1rem' }}>{error}</div>
            )}

            <button className="eh-btn" type="submit" disabled={loading}
              style={{ width: '100%', padding: '13px', background: C.ink, border: 'none', borderRadius: 10, fontSize: 14.5, fontWeight: 600, color: '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'Inter, system-ui', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Creating account…' : 'Create account'}
            </button>
          </form>

          <p style={{ fontSize: 14, color: C.sub, textAlign: 'center', marginTop: '1.5rem' }}>
            Already have an account?{' '}
            <span onClick={() => navigate('/login')} style={{ color: C.primary, fontWeight: 600, cursor: 'pointer' }}>Sign in</span>
          </p>
        </div>
      </div>
    </div>
  )
}
